import React, { useEffect, useRef, useState } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import WhiteboardController from '../../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import Tool, { type ToolButtonComponent } from '../Tool';
import ToolWireframe from './ToolWireframe';
import ToolbarMenu from '../../toolbars/ToolbarMenu';
import ToolButton from '../../toolbars/ToolButton';
import { svgToIcon } from '../../utils/svg';
import type Wireframe from '../../objects/Wireframe';
import { camelToSentenceCase } from '../../utils/string';
import { type ComponentStyles } from '../../wireframes/WireframeBase';

interface PopupProps {
  setCurrentName: (name: Wireframe['wireframeName']) => void;
  selection: any;
  hidePopup: () => void;
}

const Popup: React.FC<PopupProps> = ApplicationView(
  ({ setCurrentName, selection, hidePopup }) => {
    const controller = useController(WhiteboardController);
    const states = selection
      .firstObject()
      .wireframe.wireframeNames() as ComponentStyles;
    const entries = Object.entries(states);

    const setState = (name: Wireframe['wireframeName']) => {
      setCurrentName(name);
      selection.setOnObjects('wireframeName', name);
      hidePopup();
      Tool.toolActionComplete({ controller });
    };

    return (
      <div>
        <div style={{ maxHeight: 200, overflow: 'auto' }}>
          <ToolbarMenu columns={entries.length}>
            {Object.entries(states).map(([name, icon]) => (
              <ToolButton
                key={name}
                onClick={() => setState(name)}
                name={camelToSentenceCase(name)}
              >
                {svgToIcon(icon)}
              </ToolButton>
            ))}
          </ToolbarMenu>
        </div>
      </div>
    );
  }
);

const NameButton: ToolButtonComponent = ApplicationView(
  ({ controller, selection, tool }) => {
    const tippyRef = useRef<TippyInstance>();
    const [currentName, setCurrentName] = useState(
      selection.firstObject().wireframe.wireframeName
    );
    const names = selection.firstObject().wireframe.wireframeNames();
    const { toolbarReRenderCount } = controller.state;

    useEffect(() => {
      const newName = selection.firstObject().wireframe.wireframeName;
      if (newName !== currentName) {
        setCurrentName(selection.firstObject().wireframe.wireframeName);
      }
    }, [controller, currentName, toolbarReRenderCount, selection]);

    return (
      <ToolButton
        popup={
          <Popup
            setCurrentName={setCurrentName}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        tool={tool}
        tippyRef={tippyRef}
      >
        {svgToIcon(names[currentName])}
      </ToolButton>
    );
  }
);

class ToolWireframeName extends ToolWireframe {
  static toolName = 'Style';

  static buttonComponent() {
    return NameButton;
  }
}

export default ToolWireframeName;
